import React, { useState, useEffect, useCallback } from 'react';
import { BOOKING_URL } from '../constants/config';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DentalLogoOnly from '../components/DentalLogoOnly'; // Assuming this component exists
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppLanguage } from '../hooks/useAppLanguage';
// Mock DentalLogoOnly if it's not available in this environment
// const DentalLogoOnly = ({ sx }: { sx?: any }) => <div style={sx}>LOGO</div>;


const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const { t } = useAppLanguage();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Adjust this value based on your navbar height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      handleClose();
    }
  }, []);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const timer = setTimeout(() => {
        scrollToSection(hash);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location, scrollToSection]);

  const menuItems = [
    { key: 'navbar.home', id: 'home', link: '/' },
    { key: 'navbar.ourTeam', id: 'about', link: '/#about' },
    { key: 'navbar.services', id: 'services', link: '/#services' },
    { key: 'navbar.whyUs', id: 'why-us', link: '/#why-us' },
    { key: 'navbar.contact', id: 'contact', link: '/#contact' },
  ];

    
  const handleNavigation = (link: string, sectionId: string) => {
    if (link === '/') {
      if (location.pathname === '/') {
        scrollToSection(sectionId);
      } else {
        navigate(link);
        if (sectionId === 'home') {
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
        }
      }
    } else if (link.startsWith('/#')) {
        if (location.pathname === '/') {
            scrollToSection(sectionId);
        } else {
            navigate(link);
        }
    } else {
      navigate(link);
    }
    handleClose();
  };


  return (
    <AppBar position="fixed" sx={{ bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: 1 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
        <Toolbar disableGutters>
          <DentalLogoOnly 
            sx={{ 
              display: { xs: 'flex', md: 'flex' }, 
              mr: 1, 
              fontSize: { xs: 40, md: 70 },
              color: 'primary.main' 
            }} 
          />
          <Typography
            variant={isMobile ? "body2" : "h6"}
            noWrap
            component="div"
            onClick={() => handleNavigation('/', 'home')}
            sx={{
              mr: { xs: 1, md: 2 },
              display: { xs: 'flex', md: 'flex' },
              fontFamily: 'roboto',
              fontWeight: 700,
              letterSpacing: { xs: '0rem', sm: '.2rem', md: '.3rem' },
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            PACIFICA DENTAL 
          </Typography>

          {isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => window.location.href = BOOKING_URL}
                size="small"
                sx={{ 
                  mr: 0.5,
                  display: { xs: 'block', md: 'none' },
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  },
                  color: "white",
                  whiteSpace: 'nowrap',
                  fontSize: '0.75rem',
                  padding: '4px 8px',
                }}
              >
                {t('navbar.bookNow')}
              </Button>
              <IconButton
                size="large"
                aria-label={t('navbar.aria.openMenu') || "open menu"}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="primary"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{ mt: '45px' }}
              >
                {menuItems.map((item) => (
                  <MenuItem
                    key={item.key}
                    onClick={() => handleNavigation(item.link, item.id)}
                  >
                    <Typography textAlign="center" sx={{color:"text.primary", width: '100%'}}>
                    {t(item.key)}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            // Desktop Navigation
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.key}
                  onClick={() => handleNavigation(item.link, item.id)}
                  sx={{ 
                    mx: 1,
                    '&:hover': {
                      bgcolor: 'rgba(33, 150, 243, 0.08)',
                    },
                    color: "text.primary",
                    textTransform: 'none',
                  }}
                >
                  {t(item.key)}
                </Button>
              ))}

              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.href = BOOKING_URL}
                sx={{ 
                  ml: 2,
                  display: { xs: 'none', md: 'block' },
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  },
                  color: '#FFFFFF',
                  textTransform: 'none',
                }}
              >
                {t('navbar.bookNow')}
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
